import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import { point } from "@turf/helpers";
import { LatLng } from "clients/web-app/src/types/journey";
import type { FeatureCollection } from "geojson";

export const FALLBACK_VIEW = { latitude: 50.72, longitude: -1.9, zoom: 11.2 };

export type Padding = { top: number; right: number; bottom: number; left: number };

// Generic “keep away from edge” padding for any camera fitting
export const DEFAULT_BOUNDARY_PADDING: Padding = { top: 40, right: 40, bottom: 40, left: 40 };

/**
 * Use a base padding, optionally overriding specific sides.
 */
function mergePadding(base: Padding, override?: Partial<Padding>): Padding {
  return {
    top: override?.top ?? base.top,
    right: override?.right ?? base.right,
    bottom: override?.bottom ?? base.bottom,
    left: override?.left ?? base.left,
  };
}

/**
 * Add “extra” padding on top of an existing padding.
 */
function addPadding(base: Padding, extra?: Partial<Padding>): Padding {
  return {
    top: base.top + (extra?.top ?? 0),
    right: base.right + (extra?.right ?? 0),
    bottom: base.bottom + (extra?.bottom ?? 0),
    left: base.left + (extra?.left ?? 0),
  };
}

export function isPointInsideBoundary(boundary: FeatureCollection, ll: LatLng) {
  const p = point([ll.lng, ll.lat]);
  return boundary.features.some(f => {
    const g = f.geometry;
    if (!g) return false;
    if (g.type !== "Polygon" && g.type !== "MultiPolygon") return false;
    return booleanPointInPolygon(p, f as any);
  });
}

export function flySmooth(
  map: any,
  opts: { center: [number, number]; zoom: number; duration?: number; offset?: [number, number] },
) {
  map.stop?.();

  const flyOpts: any = {
    center: opts.center,
    zoom: opts.zoom,
    duration: opts.duration ?? 650,
    essential: true,
    curve: 1.35,
    speed: 1.15,
  };

  if (opts.offset) flyOpts.offset = opts.offset;

  map.flyTo(flyOpts);
}

export function fitMapToBounds(
  map: any,
  b: { west: number; south: number; east: number; north: number },
  opts?: {
    duration?: number;
    padding?: Partial<Padding>;
    extraPadding?: Partial<Padding>;
  },
) {
  const sw: [number, number] = [b.west, b.south];
  const ne: [number, number] = [b.east, b.north];

  map.stop?.();

  const padding = addPadding(
    mergePadding(DEFAULT_BOUNDARY_PADDING, opts?.padding),
    opts?.extraPadding,
  );

  map.fitBounds([sw, ne], {
    padding,
    duration: opts?.duration ?? 650,
    maxZoom: 15,
    essential: true,
    curve: 1.35,
    speed: 1.15,
  });
}

export function cameraForBounds(
  map: any,
  b: { west: number; south: number; east: number; north: number },
  padding?: Partial<Padding>,
  extraPadding?: Partial<Padding>,
) {
  const sw: [number, number] = [b.west, b.south];
  const ne: [number, number] = [b.east, b.north];

  const finalPadding = addPadding(mergePadding(DEFAULT_BOUNDARY_PADDING, padding), extraPadding);

  const cam = map.cameraForBounds?.([sw, ne], { padding: finalPadding, maxZoom: 15 });

  const center = cam?.center;
  const zoom = cam?.zoom;

  if (!center || typeof zoom !== "number") return null;

  return { center: [center.lng, center.lat] as [number, number], zoom };
}
